import {
	createContext,
	Dispatch,
	FC,
	PropsWithChildren,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import client from "utils/client";
import { createSatelliteUrlCss } from "utils/createSatelliteUrl";
import { ExpensesData, PropertyDetails } from "utils/types";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useAuthContext } from "context/AuthContext";
import { shouldSeeProfile } from "utils/account";
import { EvidencePackage } from "app/account/properties/[id]/surveys/_surveys/evidence-package";
import { formatRelativeUrl } from "utils/formatRelativeUrl";
import { useConciergePaymentModalFlag } from "./Concierge/ConciergePaymentModal";

const CURRENT_YEAR = new Date().getFullYear();

export type PropertyModalNames =
	| "yard-sign"
	| "profile"
	| "details"
	| "edit-details"
	| "status-details"
	| "insurance-survey"
	| "insurance-waitlist"
	| "needs-attention"
	| "upload-documents"
	| "exemptions"
	| "upload-license"
	| "edit-equity-details"
	| "edit-equity-details-mortgage-paid-off"
	| "preparing-tax-data"
	| "expenses"
	| "referral"
	| "bill-reduction"
	| "more-ways-to-save"
	| "roi-calculation-explanation"
	| "insurance-contact"
	| "refinance-monitoring-survey-flow"
	| "concierge-payment";

interface IPropertyContext {
	details: PropertyDetails | undefined;
	setDetails: Dispatch<SetStateAction<PropertyDetails | undefined>>;
	satelliteUrlCss: string | undefined;
	setModalToShow: Dispatch<SetStateAction<PropertyModalNames | undefined>>;
	modalToShow: PropertyModalNames | undefined;
	loading: boolean;
	setLoading: Dispatch<SetStateAction<boolean>>;
	profileFlowProgress: number | undefined;
	profileFlowDone: boolean;
	restartSurvey: () => void;
	needsAttentionModalAppealId: number | undefined;
	setNeedsAttentionModalAppealId: Dispatch<SetStateAction<number | undefined>>;
	expensesData: ExpensesData | null;
	setExpensesData: Dispatch<SetStateAction<ExpensesData | null>>;
	loadingExpensesData: boolean;
	setLoadingExpensesData: Dispatch<SetStateAction<boolean>>;
	fetchPropertyDetails: (
		id: string,
		opts: { autoOpenNARModal: boolean }
	) => Promise<void>;
	isInOnboardingFlow: boolean;
	setIsInOnboardingFlow: Dispatch<SetStateAction<boolean>>;
}

export const PropertySurveyPhotoUploadPrompt = `Photos should be taken in ${
	CURRENT_YEAR - 1
} or ${CURRENT_YEAR}.`;

export const PropertyContext = createContext<IPropertyContext>({
	details: undefined,
	setDetails: () => {},
	satelliteUrlCss: undefined,
	setModalToShow: () => {},
	modalToShow: undefined,
	setLoading: () => {},
	loading: true,
	profileFlowProgress: undefined,
	profileFlowDone: false,
	restartSurvey: () => {},
	needsAttentionModalAppealId: undefined,
	setNeedsAttentionModalAppealId: () => {},
	expensesData: null,
	setExpensesData: () => {},
	loadingExpensesData: true,
	setLoadingExpensesData: () => {},
	fetchPropertyDetails: async () => {},
	isInOnboardingFlow: false,
	setIsInOnboardingFlow: () => {},
});

export const usePropertyContext = () => useContext(PropertyContext);

export const PropertyContextProvider: FC<PropsWithChildren<{ id: string }>> = ({
	children,
	id,
}) => {
	const [details, setDetails] = useState<PropertyDetails>();
	const [loading, setLoading] = useState(false);
	const [needsAttentionModalAppealId, setNeedsAttentionModalAppealId] =
		useState<number>();
	const [modalToShow, setModalToShow] = useState<
		PropertyModalNames | undefined
	>();
	const [expensesData, setExpensesData] = useState<ExpensesData | null>(null);
	const [loadingExpensesData, setLoadingExpensesData] = useState(true);
	const [isInOnboardingFlow, setIsInOnboardingFlow] = useState(false);

	const { isAuthenticated, properties, setProperties, userData } =
		useAuthContext();

	const satelliteUrlCss = useMemo(
		() => (details ? createSatelliteUrlCss(details) : undefined),
		[details]
	);

	const router = useRouter();
	const searchParams = useSearchParams();
	const pathname = usePathname();

	useEffect(() => {
		if (searchParams.get("onboarding")) {
			setIsInOnboardingFlow(true);
		} else {
			setIsInOnboardingFlow(false);
		}
	}, [searchParams.get("onboarding")]);

	const fetchPropertyDetails = useCallback(
		async (id: string, opts: { autoOpenNARModal: boolean }) => {
			try {
				setLoading(true);
				const res = await client.getProperty({ id });

				setDetails(res.data);

				setProperties(prevProperties => {
					const propertyIndex = prevProperties.findIndex(
						property => property.id === res.data.id
					);
					if (propertyIndex > -1) {
						const newProperties = [...prevProperties];
						newProperties[propertyIndex] = res.data;
						return newProperties;
					}

					return prevProperties;
				});

				const appealRequiringAction = res.data.appeals.find(
					appeal =>
						appeal.tax_year === res.data.tax_year &&
						appeal.needs_attention_reason.length &&
						appeal.needs_attention_reason.some(
							reason => !reason.documentsUnderReview
						)
				);

				if (
					appealRequiringAction &&
					opts.autoOpenNARModal &&
					window.location.pathname === `/account/properties/${id}` &&
					window.location.search !== `?premium-pricing=true`
				) {
					router.push(
						`/account/properties/${id}/needs-attention?appeal_id=${appealRequiringAction.appeal_id}`
					);
				}
			} catch (e) {
				console.error(e);
				router.push("/account/properties/error");
			} finally {
				setLoading(false);
			}
		},
		[userData?.id]
	);

	const { progress: profileFlowProgress, completed } =
		EvidencePackage.Survey.useFlow();
	const profileFlowDone = !!completed;
	const restartSurvey = () => {
		router.push(
			`/account/properties/${id}/surveys/evidence-package?restart=true`
		);
	};

	const { showConciergePaymentModalFlag } = useConciergePaymentModalFlag();

	useEffect(() => {
		if (isAuthenticated && userData && id)
			fetchPropertyDetails(id, { autoOpenNARModal: true });
	}, [id, isAuthenticated, fetchPropertyDetails, userData]);

	useEffect(() => {
		if (!details) return;

		if (pathname.endsWith("/survey")) {
			if (shouldSeeProfile(details)) {
				restartSurvey();
			} else {
				router.push(
					formatRelativeUrl({
						pathname: `/account/properties/${details.id}`,
						query: {
							...(searchParams.get("onboarding") ? { onboarding: true } : {}),
						},
					})
				);
			}
			return;
		}
		if (pathname.endsWith("/needs-attention")) {
			const needsAttentionAppealId = parseInt(
				searchParams.get("appeal_id") || "0"
			);
			if (searchParams.get("appeal_id") && needsAttentionAppealId) {
				setNeedsAttentionModalAppealId(needsAttentionAppealId);
				setModalToShow("needs-attention");
			}
			return;
		}

		if (pathname.endsWith("/exemptions")) {
			setModalToShow("exemptions");
			return;
		}

		if (pathname.endsWith("/yard-sign")) {
			setModalToShow("yard-sign");
			return;
		}

		if (pathname.endsWith("/expenses") && searchParams.get("edit") === "true") {
			setModalToShow("expenses");
			return;
		}

		if (pathname.endsWith("/insurance")) {
			setModalToShow("insurance-survey");
			return;
		}

		if (pathname.endsWith("/files")) {
			setModalToShow("upload-documents");
			return;
		}

		if (searchParams.get("exemptionsnar") === "dl") {
			setModalToShow("upload-license");
			return;
		}

		if (searchParams.get("exemptionsnar") === "ny-tax") {
			setModalToShow("exemptions");
			return;
		}

		if (searchParams.get("exemptionsnar") === "ny-invalid-credentials") {
			setModalToShow("exemptions");
			return;
		}

		if (searchParams.get("bill-reduction")) {
			setModalToShow("bill-reduction");
			return;
		}

		if (showConciergePaymentModalFlag == true) {
			setModalToShow("concierge-payment");
			return;
		}

		if (showConciergePaymentModalFlag == false) {
			setModalToShow(undefined);
			return;
		}

		// When user navigates back, unset modalToShow
		setModalToShow(undefined);
	}, [details, pathname, searchParams, showConciergePaymentModalFlag]); // Listen for changes to router.asPath

	useEffect(() => {
		setDetails(undefined);
		setModalToShow(undefined);
		setNeedsAttentionModalAppealId(undefined);
	}, [id]);

	return (
		<PropertyContext.Provider
			value={{
				details,
				setDetails,
				satelliteUrlCss,
				loading,
				setLoading,
				profileFlowDone,
				profileFlowProgress,
				setModalToShow,
				modalToShow,
				restartSurvey,
				needsAttentionModalAppealId,
				setNeedsAttentionModalAppealId,
				expensesData,
				setExpensesData,
				loadingExpensesData,
				setLoadingExpensesData,
				fetchPropertyDetails,
				isInOnboardingFlow,
				setIsInOnboardingFlow,
			}}>
			{children}
		</PropertyContext.Provider>
	);
};
